import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Chip, TextField, Divider,
  IconButton, Alert, CircularProgress, Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { getCategoryColor, getCategoryLabel, getCapacityColor, getRsvpSuffix } from './constants';

// ─── Visual table layout ────────────────────────────────────────────────────────

const CONTAINER = 300;

function getSeatPositionRound(index, total, tableRadius) {
  const angle = (2 * Math.PI * index) / total - Math.PI / 2;
  const r = tableRadius + 26;
  return {
    x: CONTAINER / 2 + r * Math.cos(angle),
    y: CONTAINER / 2 + r * Math.sin(angle),
  };
}

function getSeatPositionRect(index, total, tw, th) {
  if (total === 0) return { x: CONTAINER / 2, y: CONTAINER / 2 };
  const x0 = CONTAINER / 2 - tw / 2;
  const y0 = CONTAINER / 2 - th / 2;
  const OFFSET = 22;

  // Distribute seats proportionally per side, centered within each side
  const perimeter = 2 * (tw + th);
  const topCount = Math.max(1, Math.round(total * tw / perimeter));
  const bottomCount = Math.max(1, Math.round(total * tw / perimeter));
  const remaining = Math.max(0, total - topCount - bottomCount);
  const leftCount = Math.floor(remaining / 2);
  const rightCount = remaining - leftCount;

  const positions = [];
  // Top: left → right
  for (let i = 0; i < topCount; i++)
    positions.push({ x: x0 + tw * (i + 1) / (topCount + 1), y: y0 - OFFSET });
  // Right: top → bottom
  for (let i = 0; i < rightCount; i++)
    positions.push({ x: x0 + tw + OFFSET, y: y0 + th * (i + 1) / (rightCount + 1) });
  // Bottom: right → left
  for (let i = bottomCount - 1; i >= 0; i--)
    positions.push({ x: x0 + tw * (i + 1) / (bottomCount + 1), y: y0 + th + OFFSET });
  // Left: bottom → top
  for (let i = leftCount - 1; i >= 0; i--)
    positions.push({ x: x0 - OFFSET, y: y0 + th * (i + 1) / (leftCount + 1) });

  return positions[index % positions.length] ?? { x: CONTAINER / 2, y: CONTAINER / 2 };
}

function VisualSeatDot({ tableId, seatNumber, guest, activeFilter, onUnassign }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `seat-v-${tableId}-${seatNumber}`,
    data: { type: 'SEAT', tableId, seatNumber },
  });

  const color = guest
    ? getCategoryColor(activeFilter === 'none' ? 'default' : (guest.guestCategory || 'default'))
    : isOver
      ? '#a5d6a7'
      : '#f0f0f0';

  const label = guest ? `${guest.lastName}\n${guest.firstName}` : String(seatNumber);

  return (
    <Tooltip
      title={
        guest
          ? <Box>
              <Typography variant="caption" fontWeight={700} display="block">{guest.lastName} {guest.firstName}</Typography>
              {guest.dietaryRestrictions && <Typography variant="caption" display="block" sx={{ color: '#ffcc80' }}>Allergia: {guest.dietaryRestrictions}</Typography>}
              {guest.guestCategory && <Typography variant="caption" display="block">Kat.: {getCategoryLabel(guest.guestCategory)}</Typography>}
            </Box>
          : `${seatNumber}. hely (szabad – húzz ide)`
      }
      arrow
      placement="top"
    >
      <Box
        ref={setNodeRef}
        sx={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          bgcolor: color,
          border: isOver
            ? '2px dashed #2a5d32'
            : guest
              ? '2px solid rgba(0,0,0,0.18)'
              : '1.5px dashed #bdbdbd',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: guest ? 'default' : 'copy',
          position: 'relative',
          transition: 'all 0.12s',
          '&:hover .seat-unassign': { display: 'flex' },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: guest ? 8 : 10,
            lineHeight: 1.1,
            textAlign: 'center',
            color: guest ? '#212121' : '#9e9e9e',
            fontWeight: guest ? 700 : 400,
            px: 0.25,
            whiteSpace: 'pre-line',
          }}
        >
          {label}
        </Typography>

        {/* Unassign button on hover */}
        {guest && onUnassign && (
          <IconButton
            className="seat-unassign"
            size="small"
            onClick={(e) => { e.stopPropagation(); onUnassign(); }}
            sx={{
              display: 'none',
              position: 'absolute',
              top: -10,
              right: -10,
              width: 18,
              height: 18,
              bgcolor: '#ef5350',
              color: '#fff',
              '&:hover': { bgcolor: '#c62828' },
              zIndex: 5,
            }}
          >
            <CloseIcon sx={{ fontSize: 10 }} />
          </IconButton>
        )}
      </Box>
    </Tooltip>
  );
}

function TableLayoutVisual({ tableId, shape, capacity, seats, activeFilter, onUnassign }) {
  const isRound = shape === 'round';
  const tableW = isRound ? 130 : 190;
  const tableH = isRound ? 130 : 95;
  const tableRadius = isRound ? tableW / 2 : undefined;

  return (
    <Box sx={{ position: 'relative', width: CONTAINER, height: CONTAINER, flexShrink: 0 }}>
      {/* Table body */}
      <Box
        sx={{
          position: 'absolute',
          left: CONTAINER / 2 - tableW / 2,
          top: CONTAINER / 2 - tableH / 2,
          width: tableW,
          height: tableH,
          borderRadius: isRound ? '50%' : 2,
          background: isRound
            ? 'radial-gradient(circle, #e8f5e9 60%, #c8e6c9 100%)'
            : '#e8f5e9',
          border: '2px solid #a5d6a7',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Typography variant="caption" fontWeight={700} color="#2a5d32" sx={{ fontSize: 13 }}>
          {seats.filter(Boolean).length}/{capacity}
        </Typography>
        <Typography variant="caption" color="#4caf50" sx={{ fontSize: 10 }}>
          {isRound ? 'Kerek asztal' : 'Négyszög asztal'}
        </Typography>
      </Box>

      {/* Seat dots positioned around the table */}
      {seats.map((guest, i) => {
        const pos = isRound
          ? getSeatPositionRound(i, capacity, tableRadius)
          : getSeatPositionRect(i, capacity, tableW, tableH);
        return (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: pos.x - 19,
              top: pos.y - 19,
            }}
          >
            <VisualSeatDot
              tableId={tableId}
              seatNumber={i + 1}
              guest={guest}
              activeFilter={activeFilter}
              onUnassign={guest ? () => onUnassign(guest.id) : undefined}
            />
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Main dialog ───────────────────────────────────────────────────────────────

/**
 * Dialog showing a table's details: visual seat layout + unassigned guest list.
 *
 * Props:
 *   open
 *   onClose
 *   dbTable           – { id, tableName, capacity, tableType, guests, guestCount }
 *   unassignedGuests  – WeddingGuest[]
 *   activeFilter
 *   onAssignToSeat    – (guestId, tableId, seatNumber) => Promise
 *   onUnassign        – (guestId) => Promise
 *   onRename          – (tableId, newName) => Promise
 *   onDelete          – (tableId) => void
 *   onUpdateCapacity  – (tableId, capacity) => Promise
 */
export default function TableDetailDialog({
  open,
  onClose,
  dbTable,
  unassignedGuests,
  activeFilter,
  onAssignToSeat,
  onUnassign,
  onRename,
  onDelete,
  onUpdateCapacity,
  container,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [editingCapacity, setEditingCapacity] = useState(false);
  const [capValue, setCapValue] = useState('');
  const [activeGuestId, setActiveGuestId] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  if (!dbTable) return null;

  const { id: tableId, tableName, capacity, guests = [], tableType } = dbTable;
  const guestCount = guests.length;
  const capacityColor = getCapacityColor(guestCount, capacity);

  // Determine shape from tableType
  const shape = tableType === 'Kerek' ? 'round' : 'rect';

  // Map guests by seatNumber
  const guestBySeat = {};
  guests.forEach(g => {
    if (g.seatNumber) guestBySeat[g.seatNumber] = g;
  });
  const unseatedAtTable = guests.filter(g => !g.seatNumber);
  const seats = Array.from({ length: capacity }, (_, i) => {
    const seatNum = i + 1;
    return guestBySeat[seatNum] || unseatedAtTable.shift() || null;
  });

  const activeGuest = activeGuestId
    ? unassignedGuests.find(g => g.id === activeGuestId)
    : null;

  const handleDragStart = ({ active }) => {
    setActiveGuestId(active.data.current?.guestId ?? null);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveGuestId(null);
    if (!over) return;
    if (over.data?.current?.type !== 'SEAT') return;

    const guestId = active.data.current?.guestId;
    const { seatNumber } = over.data.current;
    const seatGuest = seats[seatNumber - 1];
    if (!guestId || seatGuest) return;

    setAssigning(true);
    setAssignError('');
    try {
      await onAssignToSeat(guestId, tableId, seatNumber);
    } catch (err) {
      setAssignError(err.message || 'Hiba a hozzárendelés során');
    } finally {
      setAssigning(false);
    }
  };

  const handleNameSave = async () => {
    setEditingName(false);
    if (nameValue.trim() && nameValue.trim() !== tableName) {
      await onRename(tableId, nameValue.trim());
    }
  };

  const handleCapacitySave = async () => {
    setEditingCapacity(false);
    const parsed = parseInt(capValue, 10);
    if (!isNaN(parsed) && parsed > 0 && parsed !== capacity) {
      await onUpdateCapacity(tableId, parsed);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      container={container}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, maxHeight: isMobile ? '100vh' : '90vh' } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px',
            bgcolor: alpha('#fff', 0.15),
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <TableRestaurantIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, flexWrap: 'wrap' }}>
            {editingName ? (
              <TextField
                value={nameValue}
                onChange={e => setNameValue(e.target.value)}
                onBlur={handleNameSave}
                onKeyDown={e => { if (e.key === 'Enter') handleNameSave(); if (e.key === 'Escape') setEditingName(false); }}
                autoFocus
                size="small"
                sx={{
                  width: 160,
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: alpha('#fff', 0.5) },
                    '&:hover fieldset': { borderColor: '#fff' },
                  },
                }}
              />
            ) : (
              <Typography variant="h6" sx={{ color: '#fff', lineHeight: 1.2 }}>{tableName}</Typography>
            )}
            <IconButton size="small" onClick={() => { setNameValue(tableName); setEditingName(true); }} sx={{ color: alpha('#fff', 0.7), '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.1) } }}>
              <EditIcon fontSize="small" />
            </IconButton>
            <Chip
              label={`${guestCount}/${capacity}`}
              size="small"
              sx={{ bgcolor: alpha('#fff', 0.2), color: '#fff', fontWeight: 700, border: `1px solid ${alpha('#fff', 0.3)}` }}
            />
            {editingCapacity ? (
              <TextField
                value={capValue}
                onChange={e => setCapValue(e.target.value)}
                onBlur={handleCapacitySave}
                onKeyDown={e => { if (e.key === 'Enter') handleCapacitySave(); if (e.key === 'Escape') setEditingCapacity(false); }}
                autoFocus
                size="small"
                type="number"
                sx={{
                  width: 80,
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: alpha('#fff', 0.5) },
                    '&:hover fieldset': { borderColor: '#fff' },
                  },
                }}
                inputProps={{ min: 1, max: 30 }}
              />
            ) : (
              <Button
                size="small"
                variant="text"
                sx={{ fontSize: 11, color: alpha('#fff', 0.8), '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.1) } }}
                onClick={() => { setCapValue(String(capacity)); setEditingCapacity(true); }}
              >
                Kapacitás szerkesztése
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <IconButton
              size="small"
              onClick={() => { onDelete(tableId); onClose(); }}
              title="Asztal törlése"
              sx={{ color: alpha('#fff', 0.7), '&:hover': { color: '#ff8a80', bgcolor: alpha('#fff', 0.1) } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onClose} sx={{ color: alpha('#fff', 0.7), '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.1) } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {assignError && <Alert severity="error" sx={{ mb: 1 }}>{assignError}</Alert>}
        {assigning && <CircularProgress size={20} sx={{ mb: 1 }} />}

        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'flex-start' }}>

            {/* Visual table layout */}
            <Box sx={{ alignSelf: { xs: 'center', sm: 'flex-start' }, transform: { xs: 'scale(0.82)', sm: 'none' }, transformOrigin: 'top center' }}>
              <TableLayoutVisual
                tableId={tableId}
                shape={shape}
                capacity={capacity}
                seats={seats}
                activeFilter={activeFilter}
                onUnassign={onUnassign}
              />
            </Box>

            <Divider flexItem sx={{ display: { xs: 'block', sm: 'none' } }} />
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

            {/* Right column: seated guests + unassigned guests */}
            <Box sx={{ flex: '1 1 180px', minWidth: 160, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

            {/* Already seated at this table */}
            {guests.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Asztalnál ülők ({guests.length}/{capacity})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {guests.map((g, i) => (
                    <Box
                      key={g.id ?? i}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1,
                        py: 0.4,
                        borderRadius: 1,
                        bgcolor: '#e8f5e9',
                        border: '1px solid #c8e6c9',
                      }}
                    >
                      {g.seatNumber && (
                        <Typography variant="caption" sx={{ fontSize: 9, color: '#4caf50', fontWeight: 700, minWidth: 16 }}>
                          #{g.seatNumber}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ fontSize: 11 }}>
                        {g.lastName} {g.firstName}{getRsvpSuffix(g.rsvpStatus)}
                      </Typography>
                      {onUnassign && (
                        <IconButton
                          size="small"
                          onClick={() => onUnassign(g.id)}
                          sx={{ ml: 'auto', width: 16, height: 16, color: '#ef5350' }}
                        >
                          <CloseIcon sx={{ fontSize: 10 }} />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Unassigned Guests */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Nem ültetett vendégek ({unassignedGuests.length})
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Húzd a vendéget egy helyre
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 320, overflowY: 'auto' }}>
                {unassignedGuests.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    Minden vendég ültetve van.
                  </Typography>
                ) : (
                  unassignedGuests.map(g => (
                    <DraggableGuestChip key={g.id} guest={g} />
                  ))
                )}
              </Box>
            </Box>
          </Box>
          </Box>

          <DragOverlay>
            {activeGuest && (
              <Chip
                label={`${activeGuest.lastName} ${activeGuest.firstName}`}
                size="small"
                sx={{ cursor: 'grabbing', opacity: 0.9, bgcolor: '#2a5d32', color: '#fff' }}
              />
            )}
          </DragOverlay>
        </DndContext>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={onClose}>Bezárás</Button>
      </DialogActions>
    </Dialog>
  );
}

function DraggableGuestChip({ guest }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `guest-dialog-${guest.id}`,
    data: { type: 'GUEST', guestId: guest.id },
  });

  return (
    <Chip
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      label={`${guest.lastName} ${guest.firstName}`}
      size="small"
      sx={{
        cursor: 'grab',
        opacity: isDragging ? 0.4 : 1,
        bgcolor: '#e8f5e9',
        fontSize: 11,
        width: '100%',
        justifyContent: 'flex-start',
      }}
    />
  );
}
